import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { Sparkle, Pause, Play, TrendUp, MagnifyingGlass, CaretUp, CaretDown, ArrowsClockwise } from "@phosphor-icons/react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import AIDrawer, { useAIStream } from "@/components/AIDrawer";
import { api, inr, compact, formatApiErrorDetail } from "@/lib/api";

const RANGES = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "this_week", label: "This week" },
  { value: "prev_week", label: "Previous week" },
  { value: "7d", label: "Last 7 days" },
  { value: "14d", label: "Last 14 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "month", label: "This month" },
];

const DELIVERY_STYLES = {
  ACTIVE: "bg-success/10 text-[#00953e] border-success/25",
  IN_PROCESS: "bg-warning/10 text-[#b07600] border-warning/30",
  WITH_ISSUES: "bg-red-50 text-red-500 border-red-200",
};

const DeliveryBadge = ({ value }) => (
  <Badge className={`rounded-full font-bold text-[9px] uppercase border whitespace-nowrap ${DELIVERY_STYLES[value] || "bg-[#F3F4F6] text-[#525866] border-[#E5E7EB]"}`}>
    {(value || "unknown").replace(/_/g, " ")}
  </Badge>
);

const COLUMNS = [
  { key: "name", label: "Campaign", sortable: true },
  { key: "effective_status", label: "Delivery" },
  { key: "created_at", label: "Created", sortable: true },
  { key: "daily_budget", label: "Budget/day", sortable: true },
  { key: "spend", label: "Spend", sortable: true },
  { key: "impressions", label: "Impr.", sortable: true },
  { key: "reach", label: "Reach", sortable: true },
  { key: "frequency", label: "Freq.", sortable: true },
  { key: "ctr", label: "CTR", sortable: true },
  { key: "cpc", label: "CPC", sortable: true },
  { key: "cpm", label: "CPM", sortable: true },
  { key: "leads", label: "Leads", sortable: true },
  { key: "cpl", label: "CPL", sortable: true },
  { key: "_actions", label: "Actions" },
];

export default function Campaigns() {
  const [campaigns, setCampaigns] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [accountFilter, setAccountFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [objectiveFilter, setObjectiveFilter] = useState("all");
  const [dateRange, setDateRange] = useState("14d");
  const [q, setQ] = useState("");
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("desc");
  const [drawer, setDrawer] = useState(null);
  const [applying, setApplying] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const ai = useAIStream();

  const refreshStatuses = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/meta/sync-status");
      toast.success(`Statuses refreshed from Meta — ${data.campaigns} campaign, ${data.adsets} ad set, ${data.ads} ad update(s)`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setRefreshing(false);
    }
  };

  const load = useCallback(() => {
    const params = { range: dateRange };
    if (accountFilter !== "all") params.account_id = accountFilter;
    api.get("/campaigns", { params }).then(({ data }) => setCampaigns(data)).catch(() => {});
  }, [accountFilter, dateRange]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/accounts").then(({ data }) => setAccounts(data)).catch(() => {}); }, []);

  const doAction = async (c, action) => {
    try {
      await api.post(`/campaigns/${c.id}/action`, { action });
      toast.success(
        action === "scale" ? `Budget scaled +20% for "${c.name}"` :
        action === "pause" ? `"${c.name}" paused` : `"${c.name}" resumed`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Action failed", { duration: 8000 });
    }
  };

  const analyze = (c) => {
    setDrawer({ title: c.name, subtitle: "Campaign decision · scale, pause or optimize", campaign: c });
    ai.start({ type: "campaign", entity_id: c.id });
  };

  const applyDecision = async () => {
    const action = ai.done?.decision === "SCALE" ? "scale" : "pause";
    setApplying(true);
    await doAction(drawer.campaign, action);
    setApplying(false);
    setDrawer(null);
    ai.reset();
  };

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    else { setSortKey(key); setSortDir("desc"); }
  };

  const delivery = (c) => c.effective_status || (c.status || "").toUpperCase();
  const deliveryOptions = useMemo(() => [...new Set((campaigns || []).map(delivery))].sort(), [campaigns]);
  const objectiveOptions = useMemo(() => [...new Set((campaigns || []).map((c) => c.objective))].sort(), [campaigns]);

  const visible = useMemo(() => {
    if (!campaigns) return null;
    let list = campaigns;
    const s = q.trim().toLowerCase();
    if (s) list = list.filter((c) => c.name.toLowerCase().includes(s));
    if (statusFilter !== "all") list = list.filter((c) => delivery(c) === statusFilter);
    if (objectiveFilter !== "all") list = list.filter((c) => c.objective === objectiveFilter);
    if (sortKey) {
      list = [...list].sort((a, b) => {
        const av = a[sortKey] ?? 0, bv = b[sortKey] ?? 0;
        if (typeof av === "string" || typeof bv === "string")
          return sortDir === "asc" ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
        return sortDir === "asc" ? av - bv : bv - av;
      });
    }
    return list;
  }, [campaigns, q, statusFilter, objectiveFilter, sortKey, sortDir]);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a90a0]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search campaigns…"
            data-testid="campaign-search-input" className="pl-9 w-60 rounded-lg bg-white h-10" />
        </div>
        <Select value={dateRange} onValueChange={setDateRange}>
          <SelectTrigger className="w-40 rounded-lg bg-white" data-testid="campaign-range-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {RANGES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40 rounded-lg bg-white" data-testid="campaign-status-filter">
            <SelectValue placeholder="Delivery" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All delivery</SelectItem>
            {deliveryOptions.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={objectiveFilter} onValueChange={setObjectiveFilter}>
          <SelectTrigger className="w-48 rounded-lg bg-white" data-testid="campaign-objective-filter">
            <SelectValue placeholder="Objective" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All objectives</SelectItem>
            {objectiveOptions.map((o) => <SelectItem key={o} value={o}>{o.replace(/_/g, " ")}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={accountFilter} onValueChange={setAccountFilter}>
          <SelectTrigger className="w-56 rounded-lg bg-white" data-testid="campaign-account-filter">
            <SelectValue placeholder="All accounts" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All ad accounts</SelectItem>
            {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <p className="text-sm text-[#525866] ml-auto" data-testid="campaign-count-label">
          {visible ? `${visible.length} campaigns · ${visible.filter((c) => delivery(c) === "ACTIVE").length} delivering` : " "}
        </p>
        <Button variant="outline" onClick={refreshStatuses} disabled={refreshing} data-testid="refresh-statuses-button"
          className="rounded-full h-9 px-4 text-xs font-bold border-[#E5E7EB] bg-white" title="Instantly pull live ACTIVE/PAUSED statuses for all campaigns, ad sets and ads from Meta">
          <ArrowsClockwise size={13} weight="bold" className={`mr-1.5 ${refreshing ? "animate-spin" : ""}`} /> {refreshing ? "Refreshing…" : "Refresh statuses"}
        </Button>
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-x-auto">
        {!visible ? (
          <div className="p-6 space-y-3">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-12 rounded-md" />)}</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-b border-[#E5E7EB] hover:bg-transparent">
                {COLUMNS.map((col) => (
                  <TableHead key={col.key} className="text-[10px] font-bold uppercase tracking-[0.12em] text-[#8a90a0] px-3 whitespace-nowrap">
                    {col.sortable ? (
                      <button onClick={() => toggleSort(col.key)} data-testid={`sort-${col.key}`}
                        className={`inline-flex items-center gap-1 uppercase tracking-[0.12em] font-bold hover:text-[#0A0D14] transition-colors ${sortKey === col.key ? "text-[#0052FF]" : ""}`}>
                        {col.label}
                        {sortKey === col.key && (sortDir === "desc" ? <CaretDown size={11} weight="bold" /> : <CaretUp size={11} weight="bold" />)}
                      </button>
                    ) : col.label}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {visible.map((c) => (
                <TableRow key={c.id} data-testid={`campaign-row-${c.id}`} className="border-b border-[#E5E7EB]/40 hover:bg-[#FAFAFB]">
                  <TableCell className="px-3">
                    <div className="flex items-center gap-1.5 min-w-0 max-w-[230px]">
                      <span className="font-semibold text-[13px] text-[#0A0D14] truncate" title={c.name}>{c.name}</span>
                      {(c.structure_issues || []).length > 0 && (
                        <span title={c.structure_issues.join("  ·  ")} data-testid={`structure-chip-${c.id}`}
                          className="shrink-0 inline-flex items-center rounded-full border border-red-200 bg-red-50 text-red-500 px-1.5 py-0.5 text-[9px] font-bold cursor-help">
                          ⚠ STRUCTURE
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] text-[#8a90a0]">{(c.objective || "").replace(/_/g, " ")}</div>
                  </TableCell>
                  <TableCell className="px-3"><DeliveryBadge value={delivery(c)} /></TableCell>
                  <TableCell className="font-mono text-xs px-3 whitespace-nowrap">
                    {c.created_at ? new Date(c.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                  </TableCell>
                  <TableCell className="font-mono text-xs px-3">{inr(c.daily_budget)}</TableCell>
                  <TableCell className="font-mono text-xs px-3">{inr(c.spend)}</TableCell>
                  <TableCell className="font-mono text-xs px-3">{compact(c.impressions)}</TableCell>
                  <TableCell className="font-mono text-xs px-3">{c.reach ? compact(c.reach) : "—"}</TableCell>
                  <TableCell className="font-mono text-xs px-3">{c.frequency ? c.frequency.toFixed(1) : "—"}</TableCell>
                  <TableCell className="font-mono text-xs px-3">{c.ctr}%</TableCell>
                  <TableCell className="font-mono text-xs px-3">{c.cpc ? inr(c.cpc) : "—"}</TableCell>
                  <TableCell className="font-mono text-xs px-3">{c.cpm ? inr(c.cpm) : "—"}</TableCell>
                  <TableCell className="font-mono text-xs font-bold px-3">{c.leads}</TableCell>
                  <TableCell className="font-mono text-xs px-3">{c.cpl ? inr(c.cpl) : "—"}</TableCell>
                  <TableCell className="px-3">
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="outline" onClick={() => analyze(c)} data-testid={`analyze-campaign-${c.id}`}
                        className="h-7 rounded-full border-ai/30 text-ai hover:bg-ai/5 hover:text-ai font-bold text-[11px] px-2.5">
                        <Sparkle size={12} weight="fill" className="mr-1" /> AI
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => doAction(c, "scale")} title="Scale budget +20%" data-testid={`scale-campaign-${c.id}`}
                        className="h-7 w-7 p-0 rounded-full text-[#00953e] hover:bg-success/10">
                        <TrendUp size={14} weight="bold" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => doAction(c, c.status === "active" ? "pause" : "resume")}
                        title={c.status === "active" ? "Pause" : "Resume"} data-testid={`toggle-campaign-${c.id}`}
                        className="h-7 w-7 p-0 rounded-full text-[#525866] hover:bg-[#F3F4F6]">
                        {c.status === "active" ? <Pause size={14} weight="bold" /> : <Play size={14} weight="bold" />}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <AIDrawer open={!!drawer} onClose={() => { setDrawer(null); ai.reset(); }} title={drawer?.title} subtitle={drawer?.subtitle}
        text={ai.text} running={ai.running} error={ai.error}
        decision={ai.done?.decision} onApply={drawer?.campaign ? applyDecision : undefined} applying={applying} />
    </div>
  );
}
